import { ChangeEvent, FormEvent, useCallback, useState, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileText, Sparkles, Paperclip, MessageSquareText, PenLine, ArrowRight, Check, File, X } from 'lucide-react';
import { usePrototype2 } from '../Prototype2Context';
import { NARRATIVE_OPTIONS, NarrativeOptionId } from '../prototype2Data';

const MAX_PDF_SIZE_BYTES = 10 * 1024 * 1024;

const Screen01DefineNarrative = () => {
  const {
    selectedNarrative,
    setSelectedNarrative,
    uploadedFiles,
    uploadPdfFile,
    removeUploadedFile,
    contextText,
    setContextText,
    isChatPending,
    sendChat,
    goToScreen,
    customNarrativeText,
    setCustomNarrativeText,
  } = usePrototype2();

  const [customNarrativeOpen, setCustomNarrativeOpen] = useState(
    () => selectedNarrative === null && customNarrativeText.trim() !== ''
  );
  const customTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (customNarrativeOpen) {
      customTextareaRef.current?.focus();
    }
  }, [customNarrativeOpen]);

  const canContinue =
    !isChatPending && (selectedNarrative !== null || (customNarrativeOpen && customNarrativeText.trim() !== ''));

  const handlePdfDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach((file) => uploadPdfFile(file));
    },
    [uploadPdfFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handlePdfDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxSize: MAX_PDF_SIZE_BYTES,
    multiple: true,
  });

  const handleSelectNarrative = (id: NarrativeOptionId) => {
    if (customNarrativeOpen) setCustomNarrativeOpen(false);
    setSelectedNarrative(selectedNarrative === id ? null : id);
  };

  const handleToggleCustom = () => {
    if (!customNarrativeOpen) {
      setSelectedNarrative(null);
    }
    setCustomNarrativeOpen((prev) => !prev);
  };

  const handleContextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setContextText(e.target.value);
  };

  const handleContinue = async (e: FormEvent) => {
    e.preventDefault();
    if (!canContinue) return;

    const uploadedFileNames = uploadedFiles.map((f) => f.name);

    let message: string;
    if (customNarrativeOpen && customNarrativeText.trim()) {
      const filesLine = uploadedFileNames.length ? ` I've uploaded: ${uploadedFileNames.join(', ')}.` : '';
      const contextLine = contextText.trim() ? ` Additional context: ${contextText.trim()}` : '';
      message =
        `I'd like to suggest a different narrative direction: ${customNarrativeText.trim()}.${filesLine}${contextLine}`.trim();
    } else {
      const option = NARRATIVE_OPTIONS.find((o) => o.id === selectedNarrative);
      if (!option) return;
      const filesLine = uploadedFileNames.length ? ` I've uploaded: ${uploadedFileNames.join(', ')}.` : '';
      const contextLine = contextText.trim() ? ` Additional context: ${contextText.trim()}` : '';
      message = `I'll go with Option ${option.num}: ${option.title}.${filesLine}${contextLine}`.trim();
    }

    goToScreen('02-review-outline');
    await sendChat(message, {
      intent: 'review',
      screen_id: '02-review-outline',
      ui_state: { last_user_action: 'continue-from-narrative' },
      response_hint: { shape: 'chat-reply' },
    });
  };

  return (
    <div className="flex h-full w-full bg-[#fafafa] font-['Inter']">
      <div className="flex flex-col flex-1 min-w-0">
        <header className="flex items-center gap-3 px-6 h-14 border-b border-[#e5e5e5] bg-[#fafafa] shrink-0">
          <FileText className="w-5 h-5 text-[#0a0a0a]" />
          <span className="text-[15px] font-semibold text-[#0a0a0a]">EOAA Report Builder</span>
          <span className="text-sm text-[#737373]">|</span>
          <span className="text-sm text-[#737373]">Issaquah School District - 2023-24</span>
          <div className="flex-1" />
          <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-[#f5f5f5] text-[#737373] border border-[#e5e5e5]">
            Step 1 of 3: Define Narrative
          </span>
        </header>

        <div className="flex flex-col flex-1 items-center py-10 px-6 overflow-y-auto">
          <form onSubmit={handleContinue} className="flex flex-col gap-8 w-full max-w-[720px]">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2.5">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#171717] shrink-0">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-semibold text-[#0a0a0a]">AI Draft Assistant</span>
                <span className="text-xs text-[#737373]">Just now</span>
              </div>
              <div className="flex flex-col gap-3 pl-[42px]">
                <p className="text-sm text-[#0a0a0a] leading-[1.6]">
                  Welcome! Before we start drafting, I'd like to understand the story you want this EOAA report to tell.
                  Based on the Issaquah data, here are three possible narrative directions we could take. Which one
                  resonates most — or would you like to suggest a different angle?
                </p>
                <div className="flex flex-col gap-2.5">
                  {NARRATIVE_OPTIONS.map((opt) => {
                    const selected = selectedNarrative === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => handleSelectNarrative(opt.id)}
                        className={`flex items-start gap-3.5 px-5 py-4 rounded-lg border text-left transition-colors ${
                          selected
                            ? 'bg-[#0a0a0a]/[0.03] border-[#0a0a0a]'
                            : 'bg-[#fafafa] border-[#e5e5e5] hover:border-[#a3a3a3]'
                        }`}
                      >
                        <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#171717] shrink-0 mt-0.5">
                          {selected ? (
                            <Check className="w-3.5 h-3.5 text-white" />
                          ) : (
                            <span className="text-[13px] font-bold text-white">{opt.num}</span>
                          )}
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <span className="text-sm font-semibold text-[#0a0a0a]">{opt.title}</span>
                          <p className="text-[13px] text-[#737373] leading-[1.5]">{opt.desc}</p>
                        </div>
                      </button>
                    );
                  })}

                  <div
                    className={`flex flex-col rounded-lg border border-solid transition-colors cursor-pointer ${
                      customNarrativeOpen
                        ? 'bg-[#0a0a0a]/[0.03] border-[#0a0a0a]'
                        : 'bg-[#fafafa] border-[#e5e5e5] hover:border-[#a3a3a3]'
                    }`}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={handleToggleCustom}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleToggleCustom()}
                      className="flex items-start gap-3.5 px-5 py-4"
                    >
                      <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#171717] shrink-0 mt-0.5">
                        {customNarrativeOpen && customNarrativeText.trim() ? (
                          <Check className="w-3.5 h-3.5 text-white" />
                        ) : (
                          <PenLine className="w-3.5 h-3.5 text-white" />
                        )}
                      </div>
                      <span className="text-sm font-semibold text-[#0a0a0a]">
                        Suggest a different narrative direction...
                      </span>
                    </div>
                    {customNarrativeOpen && (
                      <div className="px-5 pt-1 pb-4">
                        <textarea
                          ref={customTextareaRef}
                          value={customNarrativeText}
                          onChange={(e) => setCustomNarrativeText(e.target.value)}
                          placeholder="Describe the narrative angle you have in mind..."
                          rows={3}
                          className="w-full px-3 py-2.5 text-[13px] text-[#0a0a0a] placeholder:text-[#737373] rounded-md bg-[#fafafa] border border-[#e5e5e5] outline-none focus:border-[#a3a3a3] resize-none transition-colors"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <hr className="border-[#e5e5e5]" />

            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2.5">
                <div className="flex items-center gap-2">
                  <Paperclip className="w-4 h-4 text-[#0a0a0a]" />
                  <span className="text-sm font-semibold text-[#0a0a0a]">Upload Supporting Artifacts</span>
                  <span className="text-[13px] text-[#737373]">(optional)</span>
                </div>
                <p className="text-[13px] text-[#737373] leading-[1.5]">
                  Share previous EOAA reports, slide decks, or any other documents that would help the AI produce a more
                  accurate and contextual draft.
                </p>
                {uploadedFiles.map((f) => (
                  <div
                    key={f.id}
                    className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-md border bg-[#f5f5f5] border-[#e5e5e5]"
                  >
                    <File className="w-4 h-4 text-[#737373] shrink-0" />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-[13px] text-[#0a0a0a] truncate">{f.name}</span>
                    </div>
                    <span className="text-[12px] text-[#737373] shrink-0">{f.size}</span>
                    <button
                      type="button"
                      onClick={() => removeUploadedFile(f.id)}
                      className="hover:opacity-70 transition-opacity shrink-0"
                      aria-label={`Remove ${f.name}`}
                    >
                      <X className="w-3.5 h-3.5 text-[#737373]" />
                    </button>
                  </div>
                ))}
                <div
                  {...getRootProps()}
                  className={`flex flex-col items-center justify-center rounded-lg bg-[#fafafa] border border-dashed gap-2 transition-colors cursor-pointer ${
                    isDragActive ? 'border-[#0a0a0a] bg-[#0a0a0a]/[0.03]' : 'border-[#e5e5e5] hover:bg-gray-50'
                  } ${uploadedFiles.length ? 'h-[92px]' : 'h-[120px]'}`}
                >
                  <input {...getInputProps()} />
                  <Paperclip className={uploadedFiles.length ? 'w-4 h-4 text-[#737373]' : 'w-5 h-5 text-[#737373]'} />
                  <span className="text-[13px] text-[#737373] text-center px-4">
                    {isDragActive ? (
                      'Drop PDF files here'
                    ) : uploadedFiles.length ? (
                      <>Add another file or click to browse</>
                    ) : (
                      <>Drag and drop files here, or click to browse</>
                    )}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-2.5">
                <div className="flex items-center gap-2">
                  <MessageSquareText className="w-4 h-4 text-[#0a0a0a]" />
                  <span className="text-sm font-semibold text-[#0a0a0a]">Additional Context</span>
                  <span className="text-[13px] text-[#737373]">(optional)</span>
                </div>
                <p className="text-[13px] text-[#737373] leading-[1.5]">
                  Share anything else that would help shape the report — key stakeholder concerns, specific data points
                  to highlight, audience considerations, or prior feedback on past reports.
                </p>
                <textarea
                  value={contextText}
                  onChange={handleContextChange}
                  placeholder="Add any context here..."
                  className="w-full h-[100px] px-3 py-2.5 text-[13px] text-[#0a0a0a] placeholder:text-[#737373] rounded-md bg-[#fafafa] border border-[#e5e5e5] outline-none focus:border-[#a3a3a3] resize-none transition-colors"
                />
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={!canContinue}
                  className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#171717] text-white text-sm font-medium hover:bg-[#2a2a2a] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Screen01DefineNarrative;
